import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Button, Text, Modal, Banner, Select } from '@shopify/polaris';

import LoadingSpinner from '../../partials/LoadingSpinner';
import { PACKING_RULE_TYPE_LABELS } from '../../../warehouse/data/packingRules';
import { packedShipments, shipmentPackProgress, saveShipmentPackProgress, removeShipmentPackProgress, packStateBatchId } from '../../../warehouse/data/packedShipments';
import { useGetOrderShipmentQuery, useUploadPackingPhotoMutation, useDeletePackingPhotoMutation, useUpdateOrderStatusMutation } from '../../../warehouse/store/batches';
import { acquirePackingPhoto, uploadPackingPhoto } from '../../../warehouse/utils/packingPhoto';
import { isAuthError, SESSION_EXPIRED_MESSAGE } from '../../../warehouse/utils/errors';
import { useRequirePermission } from '../../../warehouse/utils/permissions';
import { checkboxProps, useDocumentTitle } from '../../../warehouse/utils/a11y';

// Server-matched rules for one line item - the order-shipment endpoint
// returns itemRules keyed by shipment item id
const rulesForItem = (itemRules, item) =>
  itemRules[item.id] ?? [];

// Saved checklist progress for a shipment - only trusted while the
// pack-session state belongs to this batch
const savedProgress = (batchId, orderShipmentId) =>
  packStateBatchId() === batchId ? shipmentPackProgress(orderShipmentId) : undefined;

const PackShipment = () => {
  useDocumentTitle('Pack shipment');
  const { batchId, orderId: orderIdParam, shipmentId: shipmentIdParam } = useParams();
  const navigate = useNavigate();
  // Redirects to Profile when the user lacks the packer permission
  const { loaded: permissionsLoaded, allowed } = useRequirePermission('packer');

  // orderShipmentId is "<orderId>#<shipmentId>" in the source system
  const orderShipmentId = `${orderIdParam}#${shipmentIdParam}`;
  const orderId = Number(orderIdParam);
  const shipmentId = Number(shipmentIdParam);
  const shipmentParamsValid =
    Number.isInteger(orderId) && orderId > 0 && Number.isInteger(shipmentId) && shipmentId > 0;

  // Items to pack come from the live Shopify shipment - the endpoint
  // also returns discrepancies between what was picked and the shipment's
  // current state, so a drift is caught here instead of at shipping
  // currentData (not data) so switching shipments doesn't briefly render
  // the previous shipment's checklist while the new fetch is in flight
  const {
    currentData: shipmentData,
    isFetching: isFetchingShipment,
    error: shipmentError,
    isError: isShipmentError,
  } = useGetOrderShipmentQuery(
    { batchId, orderId, shipmentId },
    { skip: !batchId || !shipmentParamsValid }
  );
  const shipment = shipmentData?.shipment ?? null;
  const discrepancies = shipmentData?.discrepancies ?? [];
  const shipmentItems = shipment?.items ?? [];
  // Rules matched server-side against this shipment's items, plus every
  // global order-shipment rule (and the built-in status rule when on)
  const itemRulesMap = shipmentData?.packingRules.itemRules ?? {};
  const shipmentRules = shipmentData?.packingRules.shipmentRules ?? [];
  // The store's order statuses - populated when the built-in
  // change_order_status rule is enabled, empty otherwise
  const orderStatuses = shipmentData?.orderStatuses ?? [];

  // Checked-off line items (item id) and rules (item id + rule id),
  // seeded from saved pack progress so leaving the screen - or parking
  // and resuming the batch - doesn't lose the work
  const [checkedItems, setCheckedItems] = useState(
    () => new Set(savedProgress(batchId, orderShipmentId)?.checkedItemIds ?? [])
  );
  const [checkedRules, setCheckedRules] = useState(
    () => new Set(savedProgress(batchId, orderShipmentId)?.checkedRuleKeys ?? [])
  );

  // take_picture photos - server records plus ones uploaded this session.
  // An uploaded photo satisfies its rule (the upload auto-checks it)
  const [localPhotos, setLocalPhotos] = useState([]);
  // Deleted this session - a cached query result can still return them
  const [removedPhotoIds, setRemovedPhotoIds] = useState(new Set());
  // A refetch can catch up to a photo uploaded this session - dedupe so it
  // doesn't render twice
  const packingPhotos = [...new Map(
    [...(shipmentData?.packingPhotos ?? []), ...localPhotos].map(p => [p.id, p])
  ).values()].filter(photo => !removedPhotoIds.has(photo.id));
  const [uploadingRuleKey, setUploadingRuleKey] = useState(null);
  const [removingPhotoId, setRemovingPhotoId] = useState(null);
  const [photoError, setPhotoError] = useState(null);
  const [uploadPackingPhotoRequest] = useUploadPackingPhotoMutation();
  const [deletePackingPhoto] = useDeletePackingPhotoMutation();

  // Order status selector for the change_order_status rule. The dropdown
  // loads with the order's current status; picking another one enables
  // the Update button. appliedStatusId remembers a successful update so
  // the button disables again - the update is optional, not a checkbox
  const [selectedStatusId, setSelectedStatusId] = useState(null);
  const [appliedStatusId, setAppliedStatusId] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [updateOrderStatus, { isLoading: isUpdatingStatus }] = useUpdateOrderStatusMutation();
  // Item ids whose rule lists are collapsed; all expanded by default
  const [collapsedItems, setCollapsedItems] = useState(new Set());

  // When on, only items that are checked but still have unchecked rules show
  const [attentionOnly, setAttentionOnly] = useState(false);

  // Barcode scanner input
  const barcodeInputRef = useRef(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [isBarcodeFocused, setIsBarcodeFocused] = useState(false);

  // Popups: quantity confirmation for a scanned item, or a no-match notice
  const [confirmItem, setConfirmItem] = useState(null);
  const [noMatchValue, setNoMatchValue] = useState(null);
  const popupOpen = confirmItem !== null || noMatchValue !== null;

  const handleBarcodeScan = (scannedBarcode) => {
    const scanValue = scannedBarcode.trim();
    setBarcodeInput('');
    if (!scanValue) return;

    const matched = shipmentItems.find(
      i => i.sku === scanValue || i.upc === scanValue
    );
    if (matched) {
      setConfirmItem(matched);
    } else {
      setNoMatchValue(scanValue);
    }
  };

  const closePopup = () => {
    setConfirmItem(null);
    setNoMatchValue(null);
    barcodeInputRef.current?.focus();
  };

  const itemRuleKey = (itemId, rule) => `${itemId}:${rule.id}`;
  const orderRuleKey = (rule) => `order:${rule.id}`;

  // Photos for a rule - item-level rules scope to the item, shipment-level
  // photos carry no item id
  const photosForRule = (rule, itemId) =>
    packingPhotos.filter(
      photo => photo.ruleId === rule.id && (itemId === undefined || photo.itemId === itemId)
    );

  // A rule is done when it's checked or - for take_picture - a photo is on
  // record (uploads auto-check their rule and survive reloads)
  const ruleDone = (ruleKey, rule, itemId) =>
    checkedRules.has(ruleKey) || photosForRule(rule, itemId).length > 0;

  const isComplete = (item, items) =>
    items.has(item.id) &&
    rulesForItem(itemRulesMap, item).every(rule => ruleDone(itemRuleKey(item.id, rule), rule, item.id));

  // Item is checked off but still has rules that haven't been checked
  const needsAttention = (item, items = checkedItems) =>
    items.has(item.id) &&
    rulesForItem(itemRulesMap, item).some(rule => !ruleDone(itemRuleKey(item.id, rule), rule, item.id));

  const attentionCount = shipmentItems.filter(item =>
    needsAttention(item)
  ).length;

  // Once nothing needs attention, turn the filter off automatically
  useEffect(() => {
    if (attentionOnly && attentionCount === 0) {
      setAttentionOnly(false);
    }
  }, [attentionOnly, attentionCount]);

  const visibleItems =
    attentionOnly && attentionCount > 0
      ? shipmentItems.filter(item => needsAttention(item))
      : shipmentItems;

  // Order (shipment-level) rules appear once every item and its rules are
  // checked off. They're the store's global order-shipment rules plus any
  // matched item rules flagged as shipment-level.
  const allItemsComplete =
    shipmentItems.length > 0 &&
    shipmentItems.every(item => isComplete(item, checkedItems));
  // Every item checked, regardless of whether its rules were completed
  const allItemsChecked =
    shipmentItems.length > 0 &&
    shipmentItems.every(item => checkedItems.has(item.id));
  // Minor rules can be overridden when everything is packed but not all
  // rules were followable - packing continues to the order rules phase
  const [rulesOverridden, setRulesOverridden] = useState(
    () => savedProgress(batchId, orderShipmentId)?.rulesOverridden ?? false
  );
  const orderPhase = allItemsComplete || (allItemsChecked && rulesOverridden);

  // Mirrors the checklist into the pack-session store so it survives the
  // screen unmounting between shipments, and rides along in the batch's
  // packing_state when the batch is parked
  const saveProgress = (
    items = checkedItems,
    rules = checkedRules,
    overridden = rulesOverridden
  ) =>
    saveShipmentPackProgress(orderShipmentId, {
      checkedItemIds: [...items],
      checkedRuleKeys: [...rules],
      rulesOverridden: overridden,
    });

  // Each shipment's checklist is seeded from its saved pack progress -
  // check-offs belong to the shipment they were made on
  useEffect(() => {
    const saved = savedProgress(batchId, orderShipmentId);
    setCheckedItems(new Set(saved?.checkedItemIds ?? []));
    setCheckedRules(new Set(saved?.checkedRuleKeys ?? []));
    setCollapsedItems(new Set());
    setLocalPhotos([]);
    setPhotoError(null);
    setRulesOverridden(saved?.rulesOverridden ?? false);
    setAttentionOnly(false);
    setSelectedStatusId(null);
    setAppliedStatusId(null);
    setStatusError('');
     
  }, [orderShipmentId, batchId]);

  const orderRules = orderPhase ? shipmentRules : [];
  // change_order_status is an optional action, not a checklist item - it
  // doesn't gate the Complete Packing button
  const completableOrderRules = orderRules.filter(rule => rule.type !== 'change_order_status');
  const orderRulesComplete =
    orderPhase &&
    completableOrderRules.every(rule => ruleDone(orderRuleKey(rule), rule));

  // The order's live status is what the selector loads with; a successful
  // update becomes the new "current" so the button disables again
  const currentStatusId = appliedStatusId ?? shipment?.status_id ?? null;
  const effectiveStatusId = selectedStatusId ?? currentStatusId;

  const handleUpdateStatus = async () => {
    if (effectiveStatusId === null || effectiveStatusId === currentStatusId || isUpdatingStatus) return;
    setStatusError('');
    const result = await updateOrderStatus({
      batchId,
      orderId,
      shipmentId,
      statusId: effectiveStatusId,
    });
    if ('error' in result) {
      setStatusError(
        isAuthError(result.error)
          ? SESSION_EXPIRED_MESSAGE
          : 'Could not update the order status. Check your connection and try again.'
      );
      return;
    }
    setAppliedStatusId(result.data.status_id);
    setSelectedStatusId(null);
  };

  // Packing is finished: record the shipment and return to this batch's
  // Pack screen so the packer can continue with the remaining shipments
  const handleCompletePacking = () => {
    packedShipments.add(orderShipmentId);
    removeShipmentPackProgress(orderShipmentId);
    navigate(`/warehouse/app/packing/${batchId}`, { replace: true });
  };

  // Auto-focus the scanner after 1s of inactivity unless a popup is open
  // or we've moved to the order rules phase (scanner is hidden then
  // anyway)
  useEffect(() => {
    if (isBarcodeFocused || popupOpen || orderPhase) return;
    const timer = setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 1000);
    return () => clearTimeout(timer);
  }, [isBarcodeFocused, popupOpen, checkedItems, checkedRules, orderPhase]);

  const toggleOrderRuleChecked = (rule) => {
    const ruleKey = orderRuleKey(rule);
    const next = new Set(checkedRules);
    if (next.has(ruleKey)) {
      next.delete(ruleKey);
    } else {
      next.add(ruleKey);
    }
    setCheckedRules(next);
    saveProgress(checkedItems, next);
  };

  const handleOverrideRules = () => {
    setRulesOverridden(true);
    saveProgress(checkedItems, checkedRules, true);
  };

  // take_picture flow: pick an image file, register it with the worker for
  // a presigned PUT URL, then upload straight to S3. A successful upload
  // checks the rule off automatically.
  const takePhotoForRule = async (rule, itemId) => {
    const ruleKey = itemId ? itemRuleKey(itemId, rule) : orderRuleKey(rule);
    setPhotoError(null);
    setUploadingRuleKey(ruleKey);
    try {
      const acquired = await acquirePackingPhoto();
      if (!acquired) return;

      const registered = await uploadPackingPhotoRequest({
        batchId,
        orderShipmentId,
        ruleId: rule.id,
        itemId,
        fileName: acquired.fileName,
      }).unwrap();

      await uploadPackingPhoto(registered.upload_url, acquired);

      const nextRules = new Set(checkedRules).add(ruleKey);
      setLocalPhotos(prev => [...prev, registered.photo]);
      setCheckedRules(nextRules);
      saveProgress(checkedItems, nextRules);
    } catch (e) {
      setPhotoError(
        isAuthError(e)
          ? SESSION_EXPIRED_MESSAGE
          : 'The photo could not be uploaded. Try again.'
      );
    } finally {
      setUploadingRuleKey(null);
    }
  };

  // Deletes a photo's S3 object and record. Removing the last photo for a
  // rule unchecks it - the rule's no longer satisfied
  const removePhoto = async (photo, rule, itemId) => {
    const ruleKey = itemId ? itemRuleKey(itemId, rule) : orderRuleKey(rule);
    setPhotoError(null);
    setRemovingPhotoId(photo.id);
    try {
      await deletePackingPhoto({
        batchId,
        orderId,
        shipmentId,
        photoId: photo.id,
      }).unwrap();

      setRemovedPhotoIds(prev => new Set(prev).add(photo.id));
      setLocalPhotos(prev => prev.filter(p => p.id !== photo.id));

      const stillCovered = packingPhotos.some(
        p => p.id !== photo.id && p.ruleId === rule.id && (itemId === undefined || p.itemId === itemId)
      );
      if (!stillCovered && checkedRules.has(ruleKey)) {
        const nextRules = new Set(checkedRules);
        nextRules.delete(ruleKey);
        setCheckedRules(nextRules);
        saveProgress(checkedItems, nextRules);
      }
    } catch (e) {
      setPhotoError(
        isAuthError(e)
          ? SESSION_EXPIRED_MESSAGE
          : 'The photo could not be removed. Try again.'
      );
    } finally {
      setRemovingPhotoId(null);
    }
  };

  // A completed item's rule list collapses; losing complete state reopens it
  const syncCollapsed = (item, items) => {
    const complete = isComplete(item, items);
    setCollapsedItems(prev => {
      const next = new Set(prev);
      if (complete) {
        next.add(item.id);
      } else {
        next.delete(item.id);
      }
      return next;
    });
  };

  const toggleItemChecked = (item) => {
    const next = new Set(checkedItems);
    if (next.has(item.id)) {
      next.delete(item.id);
    } else {
      next.add(item.id);
    }
    setCheckedItems(next);
    syncCollapsed(item, next);
    saveProgress(next);
  };

  const toggleRuleChecked = (item, rule) => {
    const ruleKey = itemRuleKey(item.id, rule);
    const next = new Set(checkedRules);
    if (next.has(ruleKey)) {
      next.delete(ruleKey);
    } else {
      next.add(ruleKey);
    }
    setCheckedRules(next);
    syncCollapsed(item, checkedItems);
    saveProgress(checkedItems, next);
  };

  const toggleCollapsed = (itemId) => {
    setCollapsedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // Confirming a quantity checks off the item only - its rules stay as-is
  const handleConfirmQuantity = () => {
    if (!confirmItem) return;
    const next = new Set(checkedItems);
    next.add(confirmItem.id);
    setCheckedItems(next);
    syncCollapsed(confirmItem, next);
    saveProgress(next);
    // Item checked with rules outstanding needs attention - filter to it
    if (needsAttention(confirmItem, next)) {
      setAttentionOnly(true);
    }
    closePopup();
  };

  // The built-in order-status rule gets a selector instead of a checkbox -
  // the dropdown loads with the order's current status and the update is
  // optional. Multi-shipment orders warn the packer to check the other
  // shipments before deciding.
  const renderStatusRuleRow = (rule) => {
    const statusChanged = effectiveStatusId !== null && effectiveStatusId !== currentStatusId;
    const consignmentCount = shipment?.shipping_consignment_count ?? 1;
    return (
      <div key={rule.id} className="warehouse-rule-row">
        <div className="warehouse-rule-name">
          [{PACKING_RULE_TYPE_LABELS[rule.type]}] {rule.name}
        </div>
        <Text as="p">{rule.description}</Text>

        <Text as="p" tone={consignmentCount > 1 ? 'caution' : 'subdued'}>
          {consignmentCount > 1
            ? `⚠ This order has ${consignmentCount} shipments - check the other shipments before deciding the order status.`
            : 'This order has 1 shipment.'}
        </Text>

        <div className="warehouse-status-row">
          <Select
            options={orderStatuses.map(status => ({ label: status.name, value: status.id }))}
            value={effectiveStatusId}
            onChange={(value) => setSelectedStatusId(value)}
            placeholder={shipment?.status ?? 'Choose a status'}
            disabled={isUpdatingStatus}
          />
          <Button
            variant="primary"
            onClick={handleUpdateStatus}
            disabled={!statusChanged || isUpdatingStatus}
            loading={isUpdatingStatus}
          >
            Update
          </Button>
        </div>
        {statusError ? (
          <Banner tone="critical"><p>{statusError}</p></Banner>
        ) : null}
      </div>
    );
  };

  // One checklist row for a rule. take_picture rows get a photo
  // upload action and show uploaded photos; add_print_item rows link to
  // the insert PDF; critical shipping requirements are highlighted -
  // missing one can ruin the shipment.
  const renderRuleRow = (rule, ruleKey, item) => {
    const itemId = item?.id;
    const ruleChecked = ruleDone(ruleKey, rule, itemId);
    const photos = photosForRule(rule, itemId);
    const isCriticalRequirement = rule.type === 'critical_shipping_requirement';
    const customerEmail = shipment?.customer_email?.trim() || '';
    const isUploading = uploadingRuleKey === ruleKey;
    // The built-in status toggle renders as a literal "true" for now
    const details = rule.type === 'change_order_status' ? 'true' : rule.description;
    return (
      <div
        key={ruleKey}
        className={`warehouse-rule-row${isCriticalRequirement ? ' warehouse-rule-critical' : ''}`}
      >
        <div
          className="warehouse-rule-main"
          {...checkboxProps(ruleChecked, () =>
            item ? toggleRuleChecked(item, rule) : toggleOrderRuleChecked(rule),
            { disabled: isUploading }
          )}
          aria-label={`${PACKING_RULE_TYPE_LABELS[rule.type]}: ${rule.name}${details ? `. ${details}` : ''}`}
        >
          <span className={`warehouse-checkbox${ruleChecked ? ' warehouse-checkbox-complete' : ''}`} aria-hidden="true">
            {ruleChecked && '✓'}
          </span>
          <span className="warehouse-rule-details">
            <span className={`warehouse-rule-name${isCriticalRequirement ? ' warehouse-rule-name-critical' : ''}`}>
              {isCriticalRequirement ? <span aria-hidden="true">⚠ </span> : ''}[{PACKING_RULE_TYPE_LABELS[rule.type]}] {rule.name}
            </span>
            <span className="warehouse-rule-text">{details}</span>
          </span>
        </div>

        {rule.type === 'take_picture' && (
          <div className="warehouse-rule-actions">
            <Button
              onClick={() => takePhotoForRule(rule, itemId)}
              disabled={isUploading}
              loading={isUploading}
            >
              Upload Photo
            </Button>
            {photos.map(photo => (
              <span key={photo.id} className="warehouse-photo-entry">
                <a href={photo.downloadUrl} target="_blank" rel="noreferrer">View photo</a>
                {removingPhotoId === photo.id ? (
                  <span className="warehouse-photo-removing">Removing...</span>
                ) : (
                  <button
                    type="button"
                    className="warehouse-link-button warehouse-link-danger"
                    onClick={() => removePhoto(photo, rule, itemId)}
                    disabled={uploadingRuleKey !== null}
                  >
                    Remove
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {rule.type === 'send_customer_email' && (
          <div className="warehouse-rule-actions">
            {customerEmail ? (
              <a href={`mailto:${customerEmail}?subject=${encodeURIComponent(`Order ${shipment?.order_id ?? ''}`)}`}>
                Email customer: {customerEmail}
              </a>
            ) : (
              <Text as="p">
                No customer email on this shipment - you'll need to find the right contact yourself.
              </Text>
            )}
          </div>
        )}

        {rule.type === 'add_print_item' && rule.fileUrl && (
          <div className="warehouse-rule-actions">
            <a href={rule.fileUrl} target="_blank" rel="noreferrer">Download print item (PDF)</a>
          </div>
        )}
      </div>
    );
  };

  const renderItem = (item) => {
    const itemRules = rulesForItem(itemRulesMap, item);
    const isChecked = checkedItems.has(item.id);
    const complete = isComplete(item, checkedItems);
    const hasProgress =
      isChecked ||
      itemRules.some(rule => checkedRules.has(itemRuleKey(item.id, rule)) || photosForRule(rule, item.id).length > 0);
    const rulesCollapsed = collapsedItems.has(item.id);

    const cardClasses = [
      'warehouse-item-card',
      complete ? 'warehouse-item-complete' : hasProgress ? 'warehouse-item-partial' : '',
    ].filter(Boolean).join(' ');

    const checkboxClasses = [
      'warehouse-checkbox',
      complete ? 'warehouse-checkbox-complete' : isChecked ? 'warehouse-checkbox-partial' : '',
    ].filter(Boolean).join(' ');

    return (
      <div key={item.id} className={cardClasses}>
        <div
          className="warehouse-item-row"
          {...checkboxProps(isChecked, () => toggleItemChecked(item))}
          aria-label={`${item.name}, quantity ${item.quantity}`}
        >
          <span className={checkboxClasses} aria-hidden="true">
            {isChecked && '✓'}
          </span>
          <span className="warehouse-item-details">
            <span className="warehouse-item-name">{item.name}</span>
            <span className="warehouse-item-meta">Qty: {item.quantity}</span>
            {item.sku && <span className="warehouse-item-meta">SKU: {item.sku}</span>}
            {item.upc && <span className="warehouse-item-meta">UPC: {item.upc}</span>}
            {item.variantOptions && item.variantOptions.length > 0 && (
              <span className="warehouse-item-meta">
                {item.variantOptions
                  .map(o => `${o.option_display_name}: ${o.label}`)
                  .join(', ')}
              </span>
            )}
          </span>
        </div>

        {itemRules.length > 0 && (
          <div className="warehouse-rules-section">
            <button
              type="button"
              className="warehouse-rules-toggle"
              onClick={() => toggleCollapsed(item.id)}
              aria-expanded={!rulesCollapsed}
            >
              <span>
                <span aria-hidden="true">{rulesCollapsed ? '▸' : '▾'}</span> {itemRules.length} rule
                {itemRules.length === 1 ? '' : 's'}
              </span>
              {complete && <span className="warehouse-rules-done">all done</span>}
            </button>
            {!rulesCollapsed &&
              itemRules.map(rule =>
                renderRuleRow(rule, itemRuleKey(item.id, rule), item)
              )}
          </div>
        )}
      </div>
    );
  };

  const discrepancyText = (d) => {
    if (d.type === 'missing_from_shipment') {
      return `${d.name ?? d.itemId} (x${d.quantity ?? '?'}) was picked but is no longer in the shipment`;
    }
    if (d.type === 'quantity_mismatch') {
      return `${d.name ?? d.itemId}: picked ${d.expected_quantity}, shipment expects ${d.actual_quantity}`;
    }
    if (d.type === 'new_in_shipment') {
      return `${d.name ?? d.itemId} (x${d.quantity ?? '?'}) was added to the shipment after picking`;
    }
    return `${d.name ?? d.itemId} (x${d.quantity ?? '?'}) was not marked as picked`;
  };

  if (!permissionsLoaded || !allowed) {
    return null;
  }

  return (
    <div className="warehouse-screen">
      <div className="warehouse-header warehouse-header-packing">
        <div className="warehouse-header-row">
          <div>
            <h2>Pack Order Shipment</h2>
            <div className="warehouse-header-subtitle">
              {shipment
                ? `Shipment ${orderShipmentId} · Order ${shipment.order_id}`
                : 'Order packing'}
            </div>
          </div>
          <Button
            onClick={() => navigate(`/warehouse/app/packing/${batchId}`, { replace: true })}
          >
            ‹ Batch
          </Button>
        </div>
      </div>

      {discrepancies.length > 0 && (
        <div className="warehouse-discrepancy-banner" role="alert">
          <div className="warehouse-discrepancy-title">
            <span aria-hidden="true">⚠ </span>{discrepancies.length} shipment {discrepancies.length === 1 ? 'mismatch' : 'mismatches'} found
          </div>
          {discrepancies.map((d, i) => (
            <div key={i}>• {discrepancyText(d)}</div>
          ))}
        </div>
      )}

      {photoError && (
        <Banner tone="critical"><p>{photoError}</p></Banner>
      )}

      {shipmentItems.length > 0 && !orderPhase && (
        <div className="warehouse-scanner">
          <div className={`warehouse-scan-indicator${isBarcodeFocused ? ' warehouse-scan-indicator-ready' : ''}`} role="status">
            {isBarcodeFocused ? 'Ready to scan' : 'Not ready to scan'}
          </div>
          <input
            ref={barcodeInputRef}
            className={`warehouse-barcode-input${isBarcodeFocused ? ' warehouse-barcode-input-focused' : ''}`}
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleBarcodeScan(e.target.value); }}
            onFocus={() => setIsBarcodeFocused(true)}
            onBlur={() => setIsBarcodeFocused(false)}
            placeholder="Click to focus, then scan barcode"
            aria-label="Barcode input - focus, then scan barcode"
            autoCapitalize="none"
            autoCorrect="off"
          />
        </div>
      )}

      {shipmentItems.length > 0 && attentionCount > 0 && (
        <button
          type="button"
          className={`warehouse-attention-toggle${attentionOnly ? ' warehouse-attention-toggle-active' : ''}`}
          onClick={() => setAttentionOnly(prev => !prev)}
          aria-pressed={attentionOnly}
        >
          <span aria-hidden="true">⚠ </span>{attentionCount} item{attentionCount === 1 ? '' : 's'} with unchecked rules
          {attentionOnly ? ' — showing only these' : ''}
        </button>
      )}

      {orderShipmentId && isFetchingShipment && !shipment ? (
        <div className="warehouse-loading"><LoadingSpinner statusText="Loading shipment..." /></div>
      ) : orderShipmentId && (isShipmentError || !shipment) ? (
        <div className="warehouse-empty">
          <h2>{isAuthError(shipmentError) ? 'Session expired' : 'Shipment Not Found'}</h2>
          <Text as="p">
            {isAuthError(shipmentError)
              ? SESSION_EXPIRED_MESSAGE
              : "This shipment couldn't be loaded from the store. It may have changed or been removed."}
          </Text>
        </div>
      ) : shipmentItems.length > 0 ? (
        <div className="warehouse-list">
          {visibleItems.map(renderItem)}

          {orderPhase && shipment ? (
            <div className="warehouse-order-section">
              <h2>Order Rules</h2>
              <div className="warehouse-order-card">
                <span className="warehouse-info-label">Ship To</span>
                <Text as="p">
                  {`${shipment.shipping_address.first_name} ${shipment.shipping_address.last_name}`}<br />
                  {shipment.shipping_address.company ? (<>{shipment.shipping_address.company}<br /></>) : null}
                  {shipment.shipping_address.street_1}<br />
                  {shipment.shipping_address.street_2 ? (<>{shipment.shipping_address.street_2}<br /></>) : null}
                  {`${shipment.shipping_address.city}, ${shipment.shipping_address.state} ${shipment.shipping_address.zip}`}<br />
                  {shipment.shipping_address.country}<br />
                  {shipment.shipping_address.phone}<br />
                  {shipment.shipping_address.email}
                </Text>
                {shipment.order_comments ? (
                  <>
                    <span className="warehouse-info-label">Order Comments</span>
                    <Text as="p">{shipment.order_comments}</Text>
                  </>
                ) : null}
                {shipment.staff_notes ? (
                  <>
                    <span className="warehouse-info-label">Staff Notes</span>
                    <Text as="p">{shipment.staff_notes}</Text>
                  </>
                ) : null}
                {shipment.shipping_method ? (
                  <>
                    <span className="warehouse-info-label">Shipping Method</span>
                    <Text as="p">{shipment.shipping_method}</Text>
                  </>
                ) : null}
              </div>
              <div className="warehouse-order-card">
                <span className="warehouse-info-label">Order Checklist</span>
                {orderRules.length > 0 ? (
                  orderRules.map(rule =>
                    rule.type === 'change_order_status'
                      ? renderStatusRuleRow(rule)
                      : renderRuleRow(rule, orderRuleKey(rule))
                  )
                ) : (
                  <Text as="p">No rules for this shipment.</Text>
                )}
              </div>
              {orderRulesComplete && (
                <Button variant="primary" onClick={handleCompletePacking}>
                  Complete Packing
                </Button>
              )}
            </div>
          ) : allItemsChecked ? (
            <div className="warehouse-order-card warehouse-override-card">
              <Text as="p">
                All items are packed, but some packing rules were not
                completed. You can override the remaining rules to continue
                with this shipment.
              </Text>
              <Button variant="primary" onClick={handleOverrideRules}>
                Override Remaining Rules
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="warehouse-empty">
          <h2>No Order Selected</h2>
          <Text as="p">Select an order from the Pack Batch screen</Text>
        </div>
      )}

      <Modal
        open={popupOpen}
        onClose={closePopup}
      >
        <Modal.Section>
        {confirmItem && (
          <>
            <h2>✓ Item Found</h2>
            <Text as="p" fontWeight="bold">{confirmItem.name}</Text>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">Quantity:</span>
              <span>{confirmItem.quantity}</span>
            </div>
            <Text as="p">
              Confirm {confirmItem.quantity} of this item{' '}
              {confirmItem.quantity === 1 ? 'is' : 'are'} packed?
            </Text>
            <div className="warehouse-popup-buttons">
              <Button onClick={closePopup}>Cancel</Button>
              <Button variant="primary" onClick={handleConfirmQuantity}>Confirm Packed</Button>
            </div>
          </>
        )}
        {noMatchValue !== null && (
          <>
            <h2>No Match</h2>
            <Text as="p">
              No item in this shipment matches "{noMatchValue}" as a SKU
              or UPC.
            </Text>
            <div className="warehouse-popup-buttons">
              <Button variant="primary" onClick={closePopup}>OK</Button>
            </div>
          </>
        )}
        </Modal.Section>
      </Modal>
    </div>
  );
};

export default PackShipment;
